<template>
    <div ref="root" class="relative">
      <!-- Control -->
      <div
        class="flex items-center gap-2 px-3 py-2 border rounded-md
               focus-within:ring-2 focus-within:ring-emerald-600
               bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100"
        :class="{ 'opacity-60 cursor-not-allowed': disabled }"
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="open ? 'true' : 'false'"
        @click="toggle"
      >
        <input
        ref="input"
        :placeholder="placeholder"
        :disabled="disabled"
        v-model="query"
        class="w-full bg-transparent outline-none dark:placeholder:text-gray-400"
        @keydown.stop="onKeydown"   
        @input.stop="open = true"  
        />
        <button
          v-if="clearable && selectedLabel && !disabled"
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          title="Limpar seleção"
          type="button"
          @click.stop="clear"
        >
          ✕
        </button>
        <svg class="w-4 h-4 shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
        </svg>
      </div>
  
      <!-- Dropdown -->
      <div
        v-show="open"
        class="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md border bg-white
               dark:bg-slate-800 dark:border-slate-700 shadow"
        role="listbox"
        :aria-activedescendant="activeId"
      >
        <div v-if="loading" class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Carregando…</div>
        <template v-else>
          <button
            v-for="(opt, i) in filtered"
            :key="optKey(opt, i)"
            :id="optionId(i)"
            role="option"
            type="button"
            class="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-slate-700
                   focus:outline-none"
            :class="{
              'bg-gray-100 dark:bg-slate-700': i === activeIndex,
              'font-medium': valueOf(opt) === modelValue
            }"
            @mousemove="activeIndex = i"
            @mousedown.prevent="select(opt)"
          >
            {{ labelOf(opt) }}
          </button>
          <div v-if="!filtered.length" class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
            {{ noResultsText }}
          </div>
        </template>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'SearchSelect',
    props: {
      modelValue: [String, Number, null],
      options: { type: Array, default: () => [] }, // array of strings or objects
      optionLabel: { type: String, default: 'label' },
      optionValue: { type: String, default: null }, // default -> same as label if not set
      placeholder: { type: String, default: 'Selecione…' },
      disabled: { type: Boolean, default: false },
      clearable: { type: Boolean, default: false },
      loading: { type: Boolean, default: false },
      noResultsText: { type: String, default: 'Sem resultados' },
    },
    emits: ['update:modelValue'],
    data() {
      return {
        open: false,
        query: '',
        activeIndex: -1,
      };
    },
    computed: {
      selectedLabel() {
        const match = this.options.find(o => this.valueOf(o) === this.modelValue);
        return match ? this.labelOf(match) : '';
      },
      filtered() {
        const q = (this.query || '').toLowerCase();
        if (!q) return this.options;
        return this.options.filter(o => this.labelOf(o).toLowerCase().includes(q));
      },
      activeId() {
        return this.activeIndex >= 0 ? this.optionId(this.activeIndex) : null;
      },
    },
    watch: {
      // keep input showing the current selection when closed
      open(val) {
        if (!val) {
          this.query = this.selectedLabel || '';
          this.activeIndex = -1;
        } else {
          // when opening, place caret at end
          this.$nextTick(() => this.$refs.input?.focus?.());
        }
      },
      // if external model changes, mirror it in input
      modelValue() {
        if (!this.open) this.query = this.selectedLabel || '';
      },
    },
    mounted() {
      document.addEventListener('click', this.onDocClick);
      // initialize display value
      this.query = this.selectedLabel || '';
    },
    beforeUnmount() {
      document.removeEventListener('click', this.onDocClick);
    },
    methods: {
      labelOf(opt) {
        return typeof opt === 'string' ? opt : (opt?.[this.optionLabel] ?? '');
      },
      valueOf(opt) {
        if (typeof opt === 'string') return opt;
        const key = this.optionValue || this.optionLabel;
        return opt?.[key];
      },
      optKey(opt, i) {
        return (typeof opt === 'object' && (opt.id ?? opt[this.optionValue] ?? opt[this.optionLabel])) ?? i;
      },
      optionId(i) {
        return `ss-opt-${this.$._uid}-${i}`;
      },
      toggle() {
        if (this.disabled) return;
        this.open = !this.open;
      },
      onDocClick(e) {
        if (!this.$refs.root?.contains(e.target)) this.open = false;
      },
      onKeydown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.open = true;
      this.activeIndex = Math.min(
        this.activeIndex + 1,
        this.filtered.length - 1
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
    } else if (e.key === 'Enter') {
      if (this.open && this.activeIndex >= 0) {
        e.preventDefault();
        this.select(this.filtered[this.activeIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.open = false;
    } else if (e.key === 'Tab') {
      this.open = false; // no prevent: allow focus change
    } else {
      // any other key = normal typing; do not prevent default
      this.open = true;
    }
  },
      select(opt) {
        const val = this.valueOf(opt);
        this.$emit('update:modelValue', val);
        this.query = this.labelOf(opt);
        this.open = false;
      },
      clear() {
        this.$emit('update:modelValue', null);
        this.query = '';
        this.activeIndex = -1;
      },
    },
  };
  </script>
  